import { Unmasked } from "@apollo/client";
import { useMutation, useQuery } from "@apollo/client/react";
import ReactNativeFile from "@repo/graphql/client/file";
import { ChatMessageFragmentFragment } from "@repo/graphql/documents/chat/fragment.generated";
import {
  CreateMessageDocument,
  CreateMessageMutation,
} from "@repo/graphql/documents/chat/mutation.generated";
import {
  ChatMessagesDocument,
  ChatMessagesQueryVariables,
  GetChatDocument,
} from "@repo/graphql/documents/chat/query.generated";
import { UserFragmentFragment } from "@repo/graphql/documents/user/fragment.generated";
import { MontuChatMessageMessageTypeChoices } from "@repo/graphql/gql/graphql";
import MontuToastProvider from "@repo/components/src/globalProviders/toast";
import { Toast, ToastDescription } from "@repo/components/src/ui/toast";
import { stageUploadUri } from "@repo/components/src/utils/upload/stageFile";
import { ComponentProps } from "react";
import { Chat as GiftedChat } from "@kesha-antonov/react-native-chat";
import { MontuMessage } from "../../types";

function showSendFailedToast() {
  MontuToastProvider.show({
    placement: "bottom",
    duration: 3000,
    render: ({ id }) => (
      <Toast nativeID={`toast-${id}`} action="error" variant="solid">
        <ToastDescription>
          No pudimos enviar tu mensaje. Revisa tu conexión e inténtalo de nuevo.
        </ToastDescription>
      </Toast>
    ),
  });
}

type Props = {
  chatId: string;
  sentByBrand?: boolean;
  chatMessagesQueryVariables: ChatMessagesQueryVariables;
  onCompleted?: () => void;
  myUser: Unmasked<UserFragmentFragment> | null | undefined;
};

const useOnSend = (props: Props) => {
  const {
    chatId,
    sentByBrand = false,
    chatMessagesQueryVariables,
    onCompleted,
    myUser,
  } = props;
  const { data: chatData } = useQuery(GetChatDocument, {
    variables: { id: chatId },
  });
  const [sendMessage] = useMutation(CreateMessageDocument, {
    update: (cache, options) => {
      cache.updateQuery(
        {
          query: ChatMessagesDocument,
          variables: chatMessagesQueryVariables,
        },
        (prevData) => {
          if (!prevData || !options.data?.chat.createChatMessage.chatMessage)
            return null;
          const newEdges = [...prevData.chat.chatMessages.edges];
          const newChatCreated =
            options.data.chat.createChatMessage.chatMessage;
          newEdges.unshift({
            __typename: "ChatMessageTypeEdge",
            cursor: "",
            node: newChatCreated,
          });
          return {
            ...prevData,
            chat: {
              ...prevData.chat,
              chatMessages: {
                ...prevData.chat.chatMessages,
                edges: newEdges,
              },
            },
          };
        },
      );
    },
  });
  const onSend: ComponentProps<typeof GiftedChat<MontuMessage>>["onSend"] = (
    messages,
  ) => {
    onCompleted?.();
    messages.forEach(async (message) => {
      if (!myUser || !chatData?.chat.get) return;
      type N = Unmasked<ChatMessageFragmentFragment>;
      const optimisticMessage: N = {
        __typename: "ChatMessageType",
        id: String(message._id),
        createdAt: new Date(message.createdAt).toISOString(),
        message: message.text,
        isEdited: false,
        unreadByBrand: !sentByBrand,
        unreadByUser: sentByBrand,
        sentByBrand,
        upload: message.document
          ? {
              __typename: "FileFieldType",
              name: message.document.name,
              url: message.document.uri,
            }
          : null,
        uploadSize: 0,
        messageType: {
          __typename: "MontuChatMessageMessageTypeEnum",
          display: "text",
          enum: message.document?.mimeType?.startsWith("image/")
            ? MontuChatMessageMessageTypeChoices.Image
            : message.document?.mimeType?.startsWith("video/")
              ? MontuChatMessageMessageTypeChoices.Video
              : message.document?.mimeType?.startsWith("audio/")
                ? MontuChatMessageMessageTypeChoices.Audio
                : message.document?.mimeType === "application/pdf"
                  ? MontuChatMessageMessageTypeChoices.Pdf
                  : message.document?.mimeType?.match(
                        /\/(zip|x-tar|x-gzip|x-bzip2|x-7z|x-rar|gzip|compressed)/,
                      )
                    ? MontuChatMessageMessageTypeChoices.Archive
                    : message.document?.mimeType?.match(
                          /\/(msword|vnd\.openxmlformats|vnd\.ms-excel|vnd\.ms-powerpoint|spreadsheet|presentation|rtf|csv|plain|xml|json)/,
                        )
                      ? MontuChatMessageMessageTypeChoices.Document
                      : message.document
                        ? MontuChatMessageMessageTypeChoices.Other
                        : MontuChatMessageMessageTypeChoices.Text,
        },
        sent: false,
      };
      const optimisticResponse: Unmasked<CreateMessageMutation> = {
        chat: {
          __typename: "ChatMutation",
          createChatMessage: {
            __typename: "CreateChatMessage",
            chatMessage: {
              ...optimisticMessage,
              chat: {
                ...chatData.chat.get,
                brand: (() => {
                  const brand = chatData.chat.get.brand;
                  return {
                    ...brand,
                    image: brand.image,
                  };
                })(),
                lastMessage: { ...optimisticMessage },
              },
              createdBy: {
                ...myUser,
                profilePicture: myUser.profilePicture,
              },
            },
          },
        },
      };
      // The picker hands back a cache-directory URI that Android is free to
      // reclaim before (or during) the upload, which surfaced as a stream of
      // "cache/….tmp: open failed: ENOENT" unhandled rejections. Staging a
      // durable copy first, and always cleaning it up, keeps the upload
      // reading from a file that is still there.
      const staged = message.document
        ? await stageUploadUri(message.document.uri)
        : null;
      const document = message.document;
      sendMessage({
        variables: {
          chatMessage: {
            chatId,
            message: message.text,
            sentByBrand,
            upload:
              document &&
              new ReactNativeFile({
                uri: staged?.uri ?? document.uri,
                name: document.name,
                type: document.mimeType,
              }),
          },
        },
        optimisticResponse: optimisticResponse,
      })
        // `useMutation` rejects on network/upload failure. Left unhandled this
        // became an `onunhandledrejection` Sentry event on every flaky send,
        // and the user was told nothing.
        .catch(() => showSendFailedToast())
        .finally(() => staged?.cleanup());
    });
  };
  return onSend;
};

export default useOnSend;
